import useFaqT from "./useFaqT";
import { frameRect, markerCircle, markerCursor, parseMarkerSteps } from "../../utils/faqOverlay";

const ACCENT = "#1F5EFF";
const INK = "#14171C";

// Overlay renderer (design F8-B + build note 7): numbered circles, highlight
// frames and the click cursor are OVERLAY elements positioned from the
// normalized 0–1 marker JSON — CSS percentages multiply by the rendered image
// size, so nothing is ever burned into the screenshot. Marker n ↔ step n is
// 1:1; hover (desktop) and tap (mobile) both ways.
//
// kind "click" pulses its frame (tpPulse ring); `prefers-reduced-motion`
// disables the animation via Tailwind's motion-reduce variant.
// The two strings below are the LAST English-only copy on the /faq surface
// (control pass B-09). They stayed invisible because the overlay only renders
// once a marker JSON exists — i.e. from Ticket 4 on. Both now go through
// useFaqT like every other FAQ string, and FaqMarkerOverlay.jsx is registered
// in the bundle-sync test's UI_SOURCES so a future literal here is caught.
const FaqMarkerOverlay = ({ markerJson, activeN, onActivate, onDeactivate, onTap }) => {
  const tr = useFaqT();
  const steps = parseMarkerSteps(markerJson);
  if (!steps.length) return null;

  return (
    <div className="absolute inset-0" data-testid="faq-marker-overlay">
      {steps.map((m) => {
        const on = activeN === m.n;
        const frame = frameRect(m);
        const circle = markerCircle(m);
        const cursor = m.kind === "click" ? markerCursor(m) : null;
        return (
          <div key={m.n} className="absolute inset-0 pointer-events-none">
            <div
              data-testid={`faq-marker-frame-${m.n}`}
              className={`absolute border-2 ${m.kind === "click" ? "animate-faq-pulse motion-reduce:animate-none" : ""}`}
              style={{
                left: `${frame.leftPct}%`,
                top: `${frame.topPct}%`,
                width: `${frame.widthPct}%`,
                height: `${frame.heightPct}%`,
                borderColor: on ? INK : ACCENT,
                background: on ? "rgba(20,23,28,.10)" : "rgba(31,94,255,.08)",
              }}
            />
            <button
              type="button"
              data-testid={`faq-marker-${m.n}`}
              aria-label={tr("Step {{n}}", { n: m.n })}
              aria-pressed={on}
              onMouseEnter={() => onActivate && onActivate(m.n)}
              onMouseLeave={() => onDeactivate && onDeactivate()}
              onFocus={() => onActivate && onActivate(m.n)}
              onBlur={() => onDeactivate && onDeactivate()}
              onClick={() => onTap && onTap(m.n)}
              className="pointer-events-auto absolute flex h-[30px] w-[30px] -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 border-white text-[13px] font-semibold text-white shadow-[0_1px_4px_rgba(20,23,28,.3)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F5EFF] focus-visible:ring-offset-1 md:h-7 md:w-7"
              style={{
                left: `${circle.leftPct}%`,
                top: `${circle.topPct}%`,
                background: on ? INK : ACCENT,
              }}
            >
              {m.n}
            </button>
            {cursor && (
              // Near the right edge the glyph is anchored by its RIGHT side and
              // laid out in reverse, so the fixed-width "click" chip stays
              // inside the picture at 360 too (faqOverlay.js CURSOR_FLIP_AT).
              // The diamond sits on the click point in both directions.
              <div
                data-testid={`faq-cursor-${m.n}`}
                className={`absolute flex items-center gap-1.5 ${cursor.flip ? "flex-row-reverse" : ""}`}
                style={
                  cursor.flip
                    ? { right: `${100 - cursor.leftPct}%`, top: `${cursor.topPct}%` }
                    : { left: `${cursor.leftPct}%`, top: `${cursor.topPct}%` }
                }
              >
                <span className="block h-3 w-3 rotate-45 border-2 border-white bg-[#14171C] shadow-[0_1px_3px_rgba(20,23,28,.4)]" />
                <span className="bg-[#14171C] px-1.5 py-[3px] font-faqmono text-[10px] text-white">
                  {tr("click")}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FaqMarkerOverlay;
